import createCachedSelector from 're-reselect';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';

import { FeatureFlags } from '../../feature-flags/feature-flags';
import Global from '../../store/global-selectors';
import { serviceIdsEligibleForRecommendationsSelector } from './service-ids-eligible-for-recommendations';
import { EligibleToEditStatuses } from '../../enums/fulfillmentplan-order-grid-status';
import { getInsuranceProviderById } from '../../enums/insurance-providers';
import { emptyArray } from 'ui-components/default-props/empty';
import { getAlternativeRateCost, getAlternativeRateCurrencyCode } from './helpers/alternative-rate-helpers';
import { getRecommendedService } from './get-recommended-service';
import { RecommendationType } from './recommendation-type';
import { alternativeRatesRateShopperRecommendationSelector } from './selectors/alternative-rates-rate-shopper-recommendation-selector';
import { messages } from './messages';

const insuranceRecommendationSelector = createCachedSelector(
  (state) => new FeatureFlags({ state }).enableSwitchAndSaveRateRecommendations,
  (state, fulfillmentPlanId) =>
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: 'shippingConfigurationHash',
    }),
  (state, fulfillmentPlanId) =>
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: 'alternativeRates',
    }),
  /** @returns {import('./types/recommendation-types').InsuranceRecommendation | undefined} */
  (isSwitchAndSaveRecommendationsEnabled, shippingConfigurationHash, alternativeRates) => {
    if (!isSwitchAndSaveRecommendationsEnabled) {
      return;
    }

    if (shippingConfigurationHash !== alternativeRates?.shippingConfigurationHash) {
      return;
    }

    if (!alternativeRates?.insurance?.length) {
      return;
    }

    const cheapestAlternativeRate = alternativeRates.insurance.reduce((acc, alternativeRate) => {
      return getAlternativeRateCost(alternativeRate.rate) < getAlternativeRateCost(acc.rate) ? alternativeRate : acc;
    });

    return {
      insuranceProviderId: `${cheapestAlternativeRate.insuranceProviderId}`,
      carrierId: `${cheapestAlternativeRate.carrierId}`,
      serviceId: `${cheapestAlternativeRate.shippingServiceId}`,
      sellerProviderId: `${cheapestAlternativeRate.sellerProviderId}`,
      totalCost: {
        value: getAlternativeRateCost(cheapestAlternativeRate.rate),
        code: getAlternativeRateCurrencyCode(cheapestAlternativeRate.rate),
      },
    };
  },
)((_, fulfillmentPlanId) => fulfillmentPlanId);

const shippingServiceRecommendationSelector = createCachedSelector(
  (state) => new FeatureFlags({ state }).enableSwitchAndSaveRateRecommendations,
  (state, fulfillmentPlanId) =>
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: 'shippingConfigurationHash',
    }),
  (state, fulfillmentPlanId) =>
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: 'alternativeRates',
    }),
  (state) => serviceIdsEligibleForRecommendationsSelector(state),
  (state, fulfillmentPlanId) =>
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: 'serviceId',
    }),
  (state) => new FeatureFlags({ state }).enableAlternativeRatesRateShopperRecommendations,
  /** @returns {import('./types/recommendation-types').ServiceRecommendation | undefined} */
  (
    isSwitchAndSaveRecommendationsEnabled,
    fpShippingConfigurationHash,
    alternativeRates,
    serviceIdsEligibleForRecommendations,
    selectedServiceId,
    isRateShopperRecommendationsEnabled,
  ) => {
    if (!isSwitchAndSaveRecommendationsEnabled) {
      return;
    }

    if (fpShippingConfigurationHash !== alternativeRates?.shippingConfigurationHash) {
      return;
    }

    if (!alternativeRates?.service?.length) {
      return;
    }

    if (!serviceIdsEligibleForRecommendations.includes(`${selectedServiceId}`)) {
      return;
    }

    const recommendedService = getRecommendedService(alternativeRates, isRateShopperRecommendationsEnabled);

    if (!recommendedService) {
      return;
    }

    return {
      serviceId: `${recommendedService.shippingServiceId}`,
      sellerProviderId: recommendedService.sellerProviderId.toString(),
      insuranceProviderId: `${recommendedService.insuranceProviderId}`,
      carrierId: `${recommendedService.carrierId}`,
      totalCost: {
        value: getAlternativeRateCost(recommendedService.rate),
        code: getAlternativeRateCurrencyCode(recommendedService.rate),
      },
    };
  },
)((_, fulfillmentPlanId) => fulfillmentPlanId);

const alternativeRatesRecommendationSelector = createCachedSelector(
  (state, fulfillmentPlanId) =>
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: 'rateSummary.rate.totalCost',
    }),
  (state, fulfillmentPlanId) => insuranceRecommendationSelector(state, fulfillmentPlanId),
  (state, fulfillmentPlanId) => shippingServiceRecommendationSelector(state, fulfillmentPlanId),
  (state, fulfillmentPlanId) => alternativeRatesRateShopperRecommendationSelector(state, fulfillmentPlanId),
  /**
   * @param {import('../../prop-types/currency-types').Currency} rateTotalCost
   * @param {import('./types/recommendation-types').InsuranceRecommendation | undefined} insuranceRecommendation
   * @param {import('./types/recommendation-types').ServiceRecommendation | undefined} shippingServiceRecommendation
   * @param {import('./types/recommendation-types').RateShopperRecommendation | undefined} rateShopperRecommendation
   * @returns {import('./types/recommendation-types').AlternativeRatesRecommendation | undefined}
   */
  (rateTotalCost, insuranceRecommendation, shippingServiceRecommendation, rateShopperRecommendation) => {
    if (!rateTotalCost?.value) {
      return;
    }

    const isInsuranceAlternativeRateCheaper = rateTotalCost.value > insuranceRecommendation?.totalCost?.value;
    const isShippingServiceAlternativeRateCheaper = rateTotalCost.value > shippingServiceRecommendation?.totalCost?.value;
    const isRateShopperCheaper = rateTotalCost.value > rateShopperRecommendation?.totalCost?.value;

    if (!isInsuranceAlternativeRateCheaper && !isShippingServiceAlternativeRateCheaper && !isRateShopperCheaper) {
      return;
    }

    return {
      insurance: isInsuranceAlternativeRateCheaper ? insuranceRecommendation : undefined,
      service: isShippingServiceAlternativeRateCheaper ? shippingServiceRecommendation : undefined,
      rateShopper: isRateShopperCheaper ? rateShopperRecommendation : undefined,
      userRatesTotalCost: rateTotalCost,
    };
  },
)((_, fulfillmentPlanId) => fulfillmentPlanId);

/** @returns {import('./types/recommendation-types').AlternativeRatesRecommendation | undefined} */
export const useAlternativeRatesRecommendation = (fulfillmentPlanId) => {
  const alternativeRatesRecommendation = useSelector((state) => alternativeRatesRecommendationSelector(state, fulfillmentPlanId));
  const recommendedServiceCollectionGuid = alternativeRatesRecommendation?.rateShopper?.serviceCollectionGuid;
  const isRecommendedRateShopperActive = useSelector((state) => {
    if (!recommendedServiceCollectionGuid) {
      return false;
    }

    const rateShopper = Global.selectSellerServiceCollectionByGuid(state, {
      serviceCollectionGuid: recommendedServiceCollectionGuid,
    });

    return Boolean(rateShopper?.isActive);
  });

  // Rate shopper and service recommendations are mutually exclusive.
  // If there is a rate shopper recommendation and the rate shopper is active, return it and omit service.
  // If it's inactive, return service and omit rate shopper.
  return useMemo(() => {
    if (!alternativeRatesRecommendation) {
      return undefined;
    }

    const { insurance, userRatesTotalCost } = alternativeRatesRecommendation;

    if (alternativeRatesRecommendation.rateShopper && isRecommendedRateShopperActive) {
      return { insurance, userRatesTotalCost, rateShopper: alternativeRatesRecommendation.rateShopper };
    }

    return { insurance, userRatesTotalCost, service: alternativeRatesRecommendation.service };
  }, [alternativeRatesRecommendation, isRecommendedRateShopperActive]);
};

const alternativeRatesRecommendationForGridSelector = createCachedSelector(
  (state) => new FeatureFlags({ state }).enableSwitchAndSaveRateRecommendations,
  (state, fulfillmentPlanId) => alternativeRatesRecommendationSelector(state, fulfillmentPlanId),
  (state, fulfillmentPlanId) =>
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: 'orderGridStatus',
    }),
  (state, fulfillmentPlanId) =>
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: 'salesOrdersFulfilledIds',
    }),
  (state, fulfillmentPlanId) =>
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: 'labelConfiguration',
    }),
  (state, fulfillmentPlanId) =>
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: 'shippingConfigurationHash',
    }),
  (state) => Global.selectUserhasShippingRole(state),
  (state) => Global.selectSellerServices(state),
  (state) => Global.selectSellerServiceCollections(state),
  (_, fulfillmentPlanId) => fulfillmentPlanId,
  /** @returns {import('./types/recommendation-types').GridRecommendation} */
  (
    isSwitchAndSaveRecommendationsEnabled,
    alternativeRatesRecommendation,
    orderGridStatus,
    salesOrdersFulfilledIds = [],
    labelConfiguration = {},
    shippingConfigurationHash,
    userShippingRoles,
    sellerServices,
    serviceCollections = emptyArray,
    fulfillmentPlanId,
  ) => {
    const emptyResponse = {
      showAlternativeRate: false,
      salesOrdersFulfilledIds,
      labelConfiguration,
    };

    if (!isSwitchAndSaveRecommendationsEnabled) {
      return emptyResponse;
    }

    const validData = labelConfiguration && alternativeRatesRecommendation;
    const rateTotal = alternativeRatesRecommendation?.userRatesTotalCost?.value || 0;
    const isEditableFulfillmentPlan = EligibleToEditStatuses.includes(orderGridStatus);
    if (!validData || !isEditableFulfillmentPlan || !userShippingRoles || rateTotal === 0) {
      return emptyResponse;
    }

    const fromService = sellerServices.find((service) => service.serviceId === labelConfiguration.serviceId) || {};

    /** @returns {import('./types/recommendation-types').GridRecommendation} */
    const buildGridRecommendation = ({
      savingsValue,
      recommendationName,
      totalCost,
      recommendationType,
      configUpdatePayload,
      shippingServiceId,
      insuranceProviderId,
      serviceCollectionGuid,
      sellerLabelProviderId,
      carrierId,
    }) => {
      const actionLocationType = 'GRID';
      const insuranceProvider = getInsuranceProviderById(insuranceProviderId);

      return {
        labelConfiguration,
        salesOrdersFulfilledIds,
        savings: {
          value: savingsValue,
          code: alternativeRatesRecommendation?.userRatesTotalCost?.code,
        },
        showAlternativeRate: savingsValue > 0,
        recommendationName,
        shippingServiceId,
        insuranceProviderId,
        sellerLabelProviderId,
        serviceCollectionGuid,
        configUpdatePayload,
        recommendationType,
        showEventRequest: {
          fulfillmentPlanId,
          toServiceId: shippingServiceId,
          toSellerLabelProviderId: sellerLabelProviderId,
          toCarrierId: carrierId,
          toInsuranceProvider: insuranceProvider?.name,
          toServiceCollectionGuid: serviceCollectionGuid,
          recommendationType,
          toTotalCost: totalCost,
          actionLocationType,
        },
        applyEventRequest: {
          fulfillmentPlanId,
          fromServiceId: labelConfiguration?.serviceId,
          fromSellerLabelProviderId: labelConfiguration?.sellerLabelProviderId,
          fromCarrierId: fromService?.carrierId,
          fromInsuranceProvider: labelConfiguration?.insuranceProvider,
          fromTotalCost: alternativeRatesRecommendation?.userRatesTotalCost,
          fromShippingConfigurationHash: shippingConfigurationHash,
          toServiceId: shippingServiceId,
          toSellerLabelProviderId: sellerLabelProviderId,
          toCarrierId: carrierId,
          toInsuranceProvider: insuranceProvider?.name,
          toServiceCollectionGuid: serviceCollectionGuid,
          recommendationType,
          toTotalCost: totalCost,
          actionLocationType,
        },
      };
    };

    if (alternativeRatesRecommendation?.insurance) {
      const insuranceSavings = rateTotal - (alternativeRatesRecommendation?.insurance?.totalCost?.value || 0);
      const { name } = getInsuranceProviderById(alternativeRatesRecommendation?.insurance?.insuranceProviderId);
      return buildGridRecommendation({
        savingsValue: insuranceSavings,
        recommendationName: name,
        totalCost: alternativeRatesRecommendation?.insurance?.totalCost,
        recommendationType: RecommendationType.Insurance,
        shippingServiceId: alternativeRatesRecommendation?.insurance?.serviceId,
        insuranceProviderId: alternativeRatesRecommendation?.insurance?.insuranceProviderId,
        sellerLabelProviderId: alternativeRatesRecommendation?.insurance?.sellerProviderId,
        carrierId: alternativeRatesRecommendation?.insurance?.carrierId,
        configUpdatePayload: {
          insuranceProvider: name,
        },
      });
    }

    const recommendedRateShopper = alternativeRatesRecommendation?.rateShopper
      ? serviceCollections.find((c) => c.serviceCollectionGuid === alternativeRatesRecommendation.rateShopper.serviceCollectionGuid)
      : undefined;

    if (recommendedRateShopper?.isActive) {
      const rateShopperSavings = rateTotal - (alternativeRatesRecommendation?.rateShopper?.totalCost?.value || 0);
      return buildGridRecommendation({
        savingsValue: rateShopperSavings,
        recommendationName: messages.rateShopperRecommendationName,
        totalCost: alternativeRatesRecommendation?.rateShopper?.totalCost,
        recommendationType: RecommendationType.RateShopper,
        shippingServiceId: alternativeRatesRecommendation?.service?.serviceId,
        insuranceProviderId: alternativeRatesRecommendation?.service?.insuranceProviderId,
        sellerLabelProviderId: alternativeRatesRecommendation?.service?.sellerProviderId,
        carrierId: alternativeRatesRecommendation?.service?.carrierId,
        serviceCollectionGuid: alternativeRatesRecommendation.rateShopper.serviceCollectionGuid,
        configUpdatePayload: {
          serviceCollectionGuid: alternativeRatesRecommendation.rateShopper.serviceCollectionGuid,
        },
      });
    }

    if (alternativeRatesRecommendation?.service) {
      const serviceSavings = rateTotal - (alternativeRatesRecommendation?.service?.totalCost?.value || 0);
      const toService = sellerServices.find((service) => service.serviceId === alternativeRatesRecommendation?.service?.serviceId) || {};
      return buildGridRecommendation({
        savingsValue: serviceSavings,
        recommendationName: toService?.name,
        totalCost: alternativeRatesRecommendation?.service?.totalCost,
        recommendationType: RecommendationType.Service,
        shippingServiceId: alternativeRatesRecommendation?.service?.serviceId,
        insuranceProviderId: alternativeRatesRecommendation?.service?.insuranceProviderId,
        sellerLabelProviderId: toService?.sellerLabelProviderId,
        carrierId: alternativeRatesRecommendation?.service?.carrierId,
        configUpdatePayload: {
          serviceId: alternativeRatesRecommendation?.service?.serviceId,
          sellerLabelProviderId: toService?.sellerLabelProviderId,
        },
      });
    }

    return emptyResponse;
  },
)((_, fulfillmentPlanId) => fulfillmentPlanId);

/** @returns {import('./types/recommendation-types').GridRecommendation} */
export const useAlternativeRatesRecommendationForGrid = (fulfillmentPlanId) => {
  return useSelector((state) => alternativeRatesRecommendationForGridSelector(state, fulfillmentPlanId));
};
